// src/components/operationCenter/widgets/MarketPulseWidget.tsx
// Real-time market summary widget showing key market metrics
// Clean row-based layout matching the home page strip

import React, { useMemo } from "react";
import type { WidgetProps } from "@/types/dashboard";
import { useLeafCategorySnapshots } from "@/hooks/dashboard/useLeafCategorySnapshots";
import { useFearGreedChart } from "@/hooks/coinglass/useFearGreedChart";
import { useETFInflows } from "@/hooks/coinglass/useETFInflows";
import { useTokensScreenerData } from "@/hooks/tokens/useTokensScreenerData";
import { useTokensChanges } from "@/hooks/tokens/compare/useCompareData";
import { MetricKeys } from "@/constants/metrics";

function nz(n: number | null | undefined): number {
  return Number.isFinite(n as number) ? (n as number) : 0;
}

// Format large numbers with full representation (no abbreviation)
function formatFullNumber(num: number, prefix = "$"): string {
  return `${prefix}${num.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

interface StatTile {
  key: string;
  label: string;
  value: string;
  trend?: "up" | "down";
  trendValue?: string;
  subtitle?: string;
  changes?: Record<string, number>;
}

const MarketPulseWidget: React.FC<WidgetProps> = () => {
  const { leaves, isLoading } = useLeafCategorySnapshots();

  // BTC/ETH prices
  const { data: tokenRows, isLoading: tokensLoading } = useTokensScreenerData();
  const { changeByTicker } = useTokensChanges();

  // Fear & Greed
  const { data: fgData, isLoading: fgLoading } = useFearGreedChart({ limit: 1 });

  // ETF flows
  const { data: btcEtfData, isLoading: etfLoading } = useETFInflows("btc");

  // Extract BTC and ETH data
  const btcData = useMemo(() => {
    if (!tokenRows) return null;
    const row = tokenRows.find((r) => String(r["tokenSymbol"] ?? "").toUpperCase() === "BTC");
    if (!row) return null;
    const price = row[MetricKeys.PriceClose] as number | null;
    const changes = changeByTicker.get("BTC");
    const change24h = changes?.[`${MetricKeys.PriceClose}_1d`] as number | null;
    return { price, change24h };
  }, [tokenRows, changeByTicker]);

  const ethData = useMemo(() => {
    if (!tokenRows) return null;
    const row = tokenRows.find((r) => String(r["tokenSymbol"] ?? "").toUpperCase() === "ETH");
    if (!row) return null;
    const price = row[MetricKeys.PriceClose] as number | null;
    const changes = changeByTicker.get("ETH");
    const change24h = changes?.[`${MetricKeys.PriceClose}_1d`] as number | null;
    return { price, change24h };
  }, [tokenRows, changeByTicker]);

  // Fear & Greed value
  const fearGreedValue = useMemo(() => {
    if (!fgData?.series?.[0]?.data) return null;
    return fgData.series[0].data[fgData.series[0].data.length - 1] || null;
  }, [fgData]);

  const fearGreedLabel = useMemo(() => {
    if (fearGreedValue === null) return "";
    if (fearGreedValue <= 25) return "Extreme Fear";
    if (fearGreedValue <= 40) return "Fear";
    if (fearGreedValue <= 60) return "Neutral";
    if (fearGreedValue <= 75) return "Greed";
    return "Extreme Greed";
  }, [fearGreedValue]);

  // Latest ETF flow (yesterday's data usually)
  const latestEtfFlow = useMemo(() => {
    if (!btcEtfData?.length) return null;
    const latest = btcEtfData[btcEtfData.length - 1];
    if (!latest?.inflowsBySymbolUSD) return null;
    // Sum all ETF inflows for the day
    const total = Object.values(latest.inflowsBySymbolUSD).reduce((sum, val) => sum + (val || 0), 0);
    return total;
  }, [btcEtfData]);

  const stats = useMemo<StatTile[]>(() => {
    if (!leaves.length) return [];

    const clean = leaves.filter(
      (l) => nz(l.snap.current?.["market_cap"]) > 0 && l.code !== "overview"
    );

    // Total market cap
    const totalMcap = clean.reduce(
      (s, l) => s + nz(l.snap.current?.["market_cap"]),
      0
    );

    // Market cap changes for 7d, 30d, 365d
    const avgMcapChg7d =
      clean.length > 0
        ? clean.reduce(
            (s, l) =>
              s + nz((l.snap.changes?.["market_cap"]?.["7d"]?.value || 0) * 100),
            0
          ) / clean.length
        : 0;
    const avgMcapChg30d =
      clean.length > 0
        ? clean.reduce(
            (s, l) =>
              s + nz((l.snap.changes?.["market_cap"]?.["30d"]?.value || 0) * 100),
            0
          ) / clean.length
        : 0;
    const avgMcapChg365d =
      clean.length > 0
        ? clean.reduce(
            (s, l) =>
              s + nz((l.snap.changes?.["market_cap"]?.["365d"]?.value || 0) * 100),
            0
          ) / clean.length
        : 0;

    // BTC Dominance
    const btcMcap = clean
      .filter((l) => {
        const code = String(l.code || "").toUpperCase();
        const name = String(l.name || "").toUpperCase();
        return code === "BTC" || name.includes("BITCOIN");
      })
      .reduce((s, l) => s + nz(l.snap.current?.["market_cap"]), 0);
    const btcDominance = totalMcap > 0 ? (btcMcap / totalMcap) * 100 : 0;

    // Open Interest
    const totalOi = leaves.reduce(
      (s, l) => s + nz(l.snap.rollups?.["futures_oi"]?.["7d"]?.value),
      0
    );
    const avgOiChg =
      leaves.length > 0
        ? leaves.reduce(
            (s, l) =>
              s +
              nz((l.snap.changes?.["futures_oi"]?.["7d"]?.value || 0) * 100),
            0
          ) / leaves.length
        : 0;

    // Breadth
    const positives = clean.filter((r) => {
      const chg = r.snap.changes?.["market_cap"];
      return nz((chg?.["7d"]?.value || 0) * 100) > 0;
    }).length;
    const breadth = clean.length > 0 ? (positives / clean.length) * 100 : 0;

    // Avg Funding Rate
    const fundingEntries = leaves
      .map((l) => ({
        fr: nz(l.snap.current?.["futures_fr_oiw"]),
        oiWeight: nz(l.snap.current?.["futures_oi"]),
      }))
      .filter((e) => e.oiWeight > 0);
    const totalOiWeight = fundingEntries.reduce((s, e) => s + e.oiWeight, 0);
    const avgFunding =
      totalOiWeight > 0
        ? fundingEntries.reduce((s, e) => s + e.fr * e.oiWeight, 0) /
          totalOiWeight
        : 0;

    // Volume 7d + change
    const totalVol = leaves.reduce(
      (s, l) => s + nz(l.snap.rollups?.["volume"]?.["7d"]?.value),
      0
    );
    const avgVolChg =
      leaves.length > 0
        ? leaves.reduce(
            (s, l) =>
              s + nz((l.snap.changes?.["volume"]?.["7d"]?.value || 0) * 100),
            0
          ) / leaves.length
        : 0;

    return [
      {
        key: "mcap",
        label: "Total Market Cap",
        value: formatFullNumber(totalMcap),
        changes: {
          "7d": avgMcapChg7d,
          "30d": avgMcapChg30d,
          "1y": avgMcapChg365d,
        },
      },
      {
        key: "btc",
        label: "BTC Dominance",
        value: `${btcDominance.toFixed(1)}%`,
        subtitle: btcDominance >= 50 ? "BTC season" : "Alt season",
      },
      {
        key: "oi",
        label: "Total Open Interest",
        value: formatFullNumber(totalOi),
        trend: avgOiChg >= 0 ? "up" : "down",
        trendValue: `${avgOiChg >= 0 ? "+" : ""}${avgOiChg.toFixed(2)}% (7d)`,
      },
      {
        key: "breadth",
        label: "Market Breadth",
        value: `${breadth.toFixed(0)}%`,
        trend: breadth >= 50 ? "up" : "down",
        trendValue: `${positives}/${clean.length} sectors up (7d)`,
      },
      {
        key: "funding",
        label: "Avg Funding",
        value: `${avgFunding >= 0 ? "+" : ""}${avgFunding.toFixed(4)}%`,
        trend: avgFunding >= 0 ? "up" : "down",
        trendValue: avgFunding > 0.01 ? "long heavy" : avgFunding < -0.01 ? "short heavy" : "neutral",
      },
      {
        key: "vol",
        label: "Volume (7d)",
        value: formatFullNumber(totalVol),
        trend: avgVolChg >= 0 ? "up" : "down",
        trendValue: `${avgVolChg >= 0 ? "+" : ""}${avgVolChg.toFixed(2)}%`,
      },
    ] as StatTile[];
  }, [leaves]);

  const anyLoading = isLoading || tokensLoading || fgLoading || etfLoading;

  if (anyLoading && !leaves.length) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-pulse text-slate-500 text-sm">Loading...</div>
      </div>
    );
  }

  // Format price helper
  const formatPrice = (val: number | null) => {
    if (val === null) return "—";
    if (val >= 1000) return `$${val.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    if (val >= 1) return `$${val.toFixed(2)}`;
    return `$${val.toPrecision(4)}`;
  };

  const formatChange = (val: number | null) => {
    if (val === null) return "";
    return `${val >= 0 ? "+" : ""}${val.toFixed(1)}%`;
  };

  const formatETFFlow = (val: number | null) => {
    if (val === null) return "—";
    const absVal = Math.abs(val);
    const prefix = val >= 0 ? "+" : "-";
    if (absVal >= 1e9) return `${prefix}$${(absVal / 1e9).toFixed(1)}B`;
    if (absVal >= 1e6) return `${prefix}$${(absVal / 1e6).toFixed(0)}M`;
    return `${prefix}$${absVal.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  };

  // Fear & Greed color
  const fgColor = fearGreedValue === null ? "text-slate-400"
    : fearGreedValue <= 40 ? "text-emerald-400"
    : fearGreedValue <= 60 ? "text-amber-400"
    : "text-red-400";

  // Split into hero metric and other stats
  const mcapStat = stats.find((s) => s.key === "mcap");
  const otherStats = stats.filter((s) => s.key !== "mcap");

  return (
    <div className="h-full flex flex-col px-3 py-2 overflow-hidden">
      {/* Hero: Total Market Cap */}
      {mcapStat && (
        <div className="flex-shrink-0 pb-2 mb-2 border-b border-slate-700/30">
          <div className="text-xs text-slate-500 mb-0.5">Total Market Cap</div>
          <div className="text-base font-bold text-white">{mcapStat.value}</div>
          {/* 7d/30d/1y changes row */}
          {mcapStat.changes && (
            <div className="flex items-center gap-3 mt-0.5">
              {Object.entries(mcapStat.changes).map(([period, val]) => (
                <div key={period} className="text-center">
                  <span className="text-[10px] text-slate-500">{period} </span>
                  <span className={`text-xs font-semibold ${val >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {val >= 0 ? "+" : ""}{val.toFixed(1)}%
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* BTC & ETH Prices Row */}
      <div className="flex-shrink-0 flex items-center gap-4 pb-2 mb-2 border-b border-slate-700/30">
        {/* BTC */}
        <div className="flex-1">
          <div className="text-xs text-slate-500">BTC</div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-sm font-bold text-white">{formatPrice(btcData?.price ?? null)}</span>
            {btcData?.change24h !== null && btcData?.change24h !== undefined && (
              <span className={`text-xs font-medium ${btcData.change24h >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                {formatChange(btcData.change24h)}
              </span>
            )}
          </div>
        </div>
        {/* ETH */}
        <div className="flex-1">
          <div className="text-xs text-slate-500">ETH</div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-sm font-bold text-white">{formatPrice(ethData?.price ?? null)}</span>
            {ethData?.change24h !== null && ethData?.change24h !== undefined && (
              <span className={`text-xs font-medium ${ethData.change24h >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                {formatChange(ethData.change24h)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Fear & Greed + ETF Flow Row */}
      <div className="flex-shrink-0 flex items-center gap-4 pb-2 mb-2 border-b border-slate-700/30">
        {/* Fear & Greed */}
        <div className="flex-1">
          <div className="text-xs text-slate-500">Fear & Greed</div>
          <div className="flex items-baseline gap-1.5">
            <span className={`text-sm font-bold ${fgColor}`}>
              {fearGreedValue !== null ? fearGreedValue : "—"}
            </span>
            {fearGreedLabel && (
              <span className="text-xs text-slate-500">{fearGreedLabel}</span>
            )}
          </div>
        </div>
        {/* ETF Flow */}
        <div className="flex-1">
          <div className="text-xs text-slate-500">BTC ETF Flow</div>
          <div className="flex items-baseline gap-1.5">
            <span className={`text-sm font-bold ${latestEtfFlow === null ? "text-slate-400" : latestEtfFlow >= 0 ? "text-emerald-400" : "text-red-400"}`}>
              {formatETFFlow(latestEtfFlow)}
            </span>
            <span className="text-xs text-slate-500">1d</span>
          </div>
        </div>
      </div>

      {/* Stats Grid - 2 columns */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="grid grid-cols-2 gap-x-3 gap-y-2">
          {otherStats.map((stat) => (
            <div key={stat.key} className="flex flex-col">
              <span className="text-xs text-slate-500 truncate">{stat.label}</span>
              <span className="text-sm font-medium text-slate-200">{stat.value}</span>
              {stat.trend && stat.trendValue && (
                <span className={`text-[10px] ${stat.trend === "up" ? "text-emerald-400" : "text-red-400"}`}>
                  {stat.trend === "up" ? "▲" : "▼"} {stat.trendValue}
                </span>
              )}
              {stat.subtitle && !stat.trend && (
                <span className="text-[10px] text-slate-500">{stat.subtitle}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MarketPulseWidget;
